// frontend/src/components/Navbar.js
import React, { useContext, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { Menu, Transition } from "@headlessui/react";
import {
  UserCircleIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  AcademicCapIcon,
  ChartBarIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

export default function Navbar() {
  const { user, logout, isAuthenticated, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="bg-white shadow-md border-b border-yellow-500 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="/assets/img/SskruLogo.png"
            alt="SSKRU Logo"
            className="h-10 w-auto group-hover:scale-105 transition-transform"
          />
          <span className="text-xl font-bold text-yellow-700 group-hover:text-yellow-600 transition">
            SSKRU Lifelong
          </span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex gap-6 text-gray-700 font-medium items-center">
          <Link to="/" className="hover:text-yellow-600 transition px-2 py-1">
            หน้าหลัก
          </Link>
          <Link
            to="/about"
            className="hover:text-yellow-600 transition px-2 py-1"
          >
            เกี่ยวกับเรา
          </Link>
          <Link
            to="/courses"
            className="hover:text-yellow-600 transition px-2 py-1"
          >
            หลักสูตรทั้งหมด
          </Link>
          <Link
            to="/faq"
            className="hover:text-yellow-600 transition px-2 py-1"
          >
            ถาม/ตอบ
          </Link>

          {isAuthenticated ? (
            /* User Menu - แสดงเมื่อล็อกอินแล้ว */
            <Menu as="div" className="relative ml-4">
              <Menu.Button className="flex items-center space-x-2 text-gray-700 hover:text-yellow-600 focus:outline-none bg-gray-50 hover:bg-yellow-50 px-3 py-2 rounded-lg transition">
                <UserCircleIcon className="h-6 w-6 text-yellow-600" />
                <span className="hidden lg:block">
                  {user?.first_name || user?.username}
                </span>
                <ChevronDownIcon className="h-4 w-4" />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 border border-yellow-100 z-50">
                  {isAdmin && (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/admin"
                          className={`${
                            active
                              ? "bg-yellow-50 text-yellow-900"
                              : "text-gray-700"
                          } block px-4 py-2 text-sm flex items-center`}
                        >
                          <ChartBarIcon className="h-5 w-5 mr-2 text-yellow-600" />
                          จัดการระบบ
                        </Link>
                      )}
                    </Menu.Item>
                  )}

                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/user/profile"
                        className={`${
                          active
                            ? "bg-yellow-50 text-yellow-900"
                            : "text-gray-700"
                        } block px-4 py-2 text-sm flex items-center`}
                      >
                        <UserIcon className="h-5 w-5 mr-2 text-yellow-600" />
                        โปรไฟล์
                      </Link>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/user/dashboard"
                        className={`${
                          active
                            ? "bg-yellow-50 text-yellow-900"
                            : "text-gray-700"
                        } block px-4 py-2 text-sm flex items-center`}
                      >
                        <AcademicCapIcon className="h-5 w-5 mr-2 text-yellow-600" />
                        แดชบอร์ด
                      </Link>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="my-courses"
                        className={`${
                          active
                            ? "bg-yellow-50 text-yellow-900"
                            : "text-gray-700"
                        } block px-4 py-2 text-sm flex items-center`}
                      >
                        <AcademicCapIcon className="h-5 w-5 mr-2 text-yellow-600" />
                        หลักสูตรของฉัน
                      </Link>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/user/my-certificates"
                        className={`${
                          active
                            ? "bg-yellow-50 text-yellow-900"
                            : "text-gray-700"
                        } block px-4 py-2 text-sm flex items-center`}
                      >
                        <AcademicCapIcon className="h-5 w-5 mr-2 text-yellow-600" />
                        ใบประกาศ
                      </Link>
                    )}
                  </Menu.Item>

                  <div className="border-t border-gray-100 my-1"></div>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`${
                          active ? "bg-red-50 text-red-600" : "text-gray-700"
                        } block w-full text-left px-4 py-2 text-sm flex items-center`}
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2 text-red-500" />
                        ออกจากระบบ
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          ) : (
            /* Auth Buttons - แสดงเมื่อยังไม่ได้ล็อกอิน */
            <div className="flex items-center gap-3 ml-4">
              <Link
                to="/login"
                className="text-gray-700 hover:text-yellow-600 px-4 py-2 text-sm font-medium transition border border-gray-300 rounded-lg hover:border-yellow-600"
              >
                เข้าสู่ระบบ
              </Link>
              <Link
                to="/register"
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition shadow-sm hover:shadow"
              >
                สมัครสมาชิก
              </Link>
            </div>
          )}
        </div>

        {/* Hamburger Button */}
        <button
          className="md:hidden text-gray-700 p-2 hover:bg-yellow-50 rounded-lg transition"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            {isOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white px-6 pb-5 pt-2 flex flex-col gap-3 text-gray-700 font-medium shadow-lg border-t border-gray-100">
          <Link
            to="/"
            onClick={() => setIsOpen(false)}
            className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition"
          >
            หน้าหลัก
          </Link>
          <Link
            to="/about"
            onClick={() => setIsOpen(false)}
            className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition"
          >
            เกี่ยวกับเรา
          </Link>
          <Link
            to="/courses"
            onClick={() => setIsOpen(false)}
            className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition"
          >
            หลักสูตรทั้งหมด
          </Link>
          <Link
            to="/faq"
            onClick={() => setIsOpen(false)}
            className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition"
          >
            ถาม/ตอบ
          </Link>

          {isAuthenticated ? (
            <>
              <div className="border-t border-gray-100 my-1"></div>

              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsOpen(false)}
                  className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition flex items-center"
                >
                  <ChartBarIcon className="h-5 w-5 mr-2 text-yellow-600" />
                  จัดการระบบ
                </Link>
              )}

              <Link
                to="/user/dashboard"
                onClick={() => setIsOpen(false)}
                className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition flex items-center"
              >
                <AcademicCapIcon className="h-5 w-5 mr-2 text-yellow-600" />
                แดชบอร์ด
              </Link>

              <Link
                to="/user/profile"
                onClick={() => setIsOpen(false)}
                className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition flex items-center"
              >
                <UserIcon className="h-5 w-5 mr-2 text-yellow-600" />
                โปรไฟล์
              </Link>

              <Link
                to="/credit-bank"
                onClick={() => setIsOpen(false)}
                className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition flex items-center"
              >
                <AcademicCapIcon className="h-5 w-5 mr-2 text-yellow-600" />
                คลังหน่วยกิต
              </Link>

              <Link
                to="/certificates"
                onClick={() => setIsOpen(false)}
                className="hover:text-yellow-600 py-2 px-3 rounded-lg hover:bg-yellow-50 transition flex items-center"
              >
                <AcademicCapIcon className="h-5 w-5 mr-2 text-yellow-600" />
                ใบประกาศ
              </Link>

              <button
                onClick={() => {
                  handleLogout();
                  setIsOpen(false);
                }}
                className="text-left hover:text-red-600 py-2 px-3 rounded-lg hover:bg-red-50 transition flex items-center text-red-500"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                ออกจากระบบ
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-2 mt-2">
              <Link
                to="/login"
                onClick={() => setIsOpen(false)}
                className="bg-yellow-600 text-white px-4 py-3 rounded-lg text-center font-medium hover:bg-yellow-700 transition"
              >
                เข้าสู่ระบบ
              </Link>
              <Link
                to="/register"
                onClick={() => setIsOpen(false)}
                className="border-2 border-yellow-600 text-yellow-600 px-4 py-3 rounded-lg text-center font-medium hover:bg-yellow-50 transition"
              >
                สมัครสมาชิก
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
